import React, { useState } from "react";
import { Box, Typography, useTheme, Paper, Divider, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../global/Sidebar";
import Topbar from "../global/Topbar";
import { tokens } from "../../theme";
import ArticleIcon from "@mui/icons-material/Article";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CodeIcon from "@mui/icons-material/Code";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CircleIcon from '@mui/icons-material/Circle';
import ShieldIcon from '@mui/icons-material/Shield';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import DomainIcon from '@mui/icons-material/Domain';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Footer from "../global/Footer";
import KeycloakSetupGuide from "./KeycloakSetupGuide";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { IconButton, Menu, MenuItem, Button } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

const DocumentationTopbar = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t, i18n } = useTranslation();
    const { login, loading } = useAuth();
    
    // Language Menu State
    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const langOpen = Boolean(langAnchorEl);
  
    const handleLangClick = (event) => {
      setLangAnchorEl(event.currentTarget);
    };
  
    const handleLangClose = () => {
      setLangAnchorEl(null);
    };
  
    const handleLanguageChange = (language) => {
      i18n.changeLanguage(language);
      handleLangClose();
    };

    return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 4,
            height: "80px",
            backgroundColor: "var(--fx-paper)",
            borderBottom: `1px solid #ddd`,
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Box display="flex" alignItems="center" gap={3}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'var(--fx-text-primary)', 
                fontWeight: 600,
                fontSize: "1.5rem"
              }}
            >
              Documentation
            </Typography>
          </Box>
    
          <Box display="flex" alignItems="center" gap={2}>
             {/* Language Selector */}
             <IconButton
                onClick={handleLangClick}
                sx={{
                  color: 'var(--fx-text-primary)',
                  "&:hover": { 
                    backgroundColor: "rgba(18, 86, 103, 0.1)"
                  }
                }}
              >
                <LanguageIcon />
              </IconButton>
              
              <Menu
                anchorEl={langAnchorEl}
                open={langOpen}
                onClose={handleLangClose}
                sx={{
                  mt: 1,
                  "& .MuiPaper-root": {
                    backgroundColor: "var(--fx-paper)",
                    boxShadow: "0 4px 20px rgba(18, 86, 103, 0.15)",
                    borderRadius: 2,
                    minWidth: 120,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                  }
                }}
              >
                 <MenuItem onClick={() => handleLanguageChange('en')} sx={{ color: 'var(--fx-text-primary)' }}>English</MenuItem>
                 <MenuItem onClick={() => handleLanguageChange('fr')} sx={{ color: 'var(--fx-text-primary)' }}>Français</MenuItem>
              </Menu>
    
              {/* Sign In Button */}
               <Button
                  variant="outlined"
                  startIcon={loading ? <AccountCircleIcon /> : <LoginIcon />}
                  onClick={login}
                  disabled={loading}
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: colors.primary[500],
                    color: 'var(--fx-text-primary)',
                    textTransform: 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: colors.primary[600],
                      backgroundColor: 'rgba(18, 86, 103, 0.1)',
                    },
                  }}
                >
                  {loading ? t('menu.connecting') : t('menu.signIn')}
                </Button>
          </Box>
        </Box>
      );
};

const MaintenanceView = ({ title, description }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            height="100%" 
            width="100%"
            sx={{ bgcolor: "var(--fx-subtle)", p: 4 }} // Slate-50
        >
            <Paper 
                elevation={6}
                sx={{ 
                    p: 3,
                    width: "100%", 
                    maxWidth: "450px", 
                    borderRadius: "16px", // rounded-2xl
                    bgcolor: "var(--fx-paper)",
                    borderTop: "4px solid #06b6d4", // Cyan-500
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" // shadow-xl approximation
                }}
            >
                {/* Visual Icon */}
                <Box 
                    sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: "50%", 
                        bgcolor: "var(--fx-info-soft)",
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        mb: 3,
                        position: "relative",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            border: "2px solid #06b6d4",
                            animation: "pulse 2s infinite",
                        },
                        "@keyframes pulse": {
                            "0%": { transform: "scale(1)", opacity: 1 },
                            "50%": { transform: "scale(1.2)", opacity: 0.5 },
                            "100%": { transform: "scale(1)", opacity: 1 },
                        }
                    }}
                >
                    <AutoStoriesIcon sx={{ fontSize: 36, color: "#0891b2" }} /> {/* Cyan-600 */}
                </Box>

                {/* Typography */}
                <Typography variant="h5" sx={{ fontWeight: 700, color: "var(--fx-text-strong)", mb: 1 }}> {/* Slate-900 */}
                    {title || "Knowledge Base Updating"}
                </Typography>
                
                <Typography variant="body1" sx={{ color: "var(--fx-text-secondary)", mb: 4, lineHeight: 1.6 }}> {/* Slate-600 */}
                    {description || "We are currently indexing the latest API references and guides to ensure accuracy. Access will be restored shortly."}
                </Typography>

                {/* Actions */}
                <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={() => navigate('/dashboard')}
                    sx={{ 
                        bgcolor: "#0f172a", 
                        color: "#ffffff", 
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.5,
                        borderRadius: "8px",
                        mb: 3,
                        "&:hover": {
                            bgcolor: "#1e293b"
                        }
                    }}
                >
                    Return to Overview
                </Button>

                {/* System Status */}
                <Box display="flex" alignItems="center" gap={1}>
                    <CircleIcon sx={{ fontSize: 10, color: "#22c55e" }} /> {/* Green-500 */}
                    <Typography variant="caption" sx={{ color: "var(--fx-text-secondary)", fontWeight: 500 }}>
                        Core Platform Operational
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

const FAQSection = () => <MaintenanceView title="FAQ Section" description="Frequently asked questions concerning the platform usage and policies." />;
const IntroductionSection = () => <MaintenanceView title="Introduction" description="Overview of the DHDP platform, its mission, and core capabilities." />;
const GettingStartedSection = () => <MaintenanceView title="Getting Started" description="Step-by-step guides to help you set up and begin using the platform." />;

const Documentation = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const documentationItems = [
    {
      title: isAuthenticated ? "Overview" : "Home",
      path: isAuthenticated ? "/dashboard" : "/",
      icon: <ArrowBackIcon />,
      variant: "back",
    },
    { 
      title: "Introduction", 
      path: "/documentation", 
      icon: <ArticleIcon />,
    },
    { 
      title: "Platform Architecture", 
      path: "/documentation/architecture", 
      icon: <DomainIcon />,
    },
    { 
      title: "Medical Data & Use Cases", 
      path: "/documentation/medical-data", 
      icon: <MedicalServicesIcon />,
    },
    {
      title: "Security & Encryption",
      path: "/documentation/security",
      icon: <ShieldIcon />,
    },
    {
      title: "Keycloak Setup",
      path: "/documentation/keycloak-setup",
      icon: <VpnKeyIcon />,
    },
    {
      title: "FAQ",
      path: "/documentation/faq",
      icon: <HelpOutlineIcon />
    }
  ];

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar items={documentationItems} bottomItems={[]} />
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {isAuthenticated ? <Topbar /> : <DocumentationTopbar />}
        
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 4 }}>
            <Box sx={{ maxWidth: "1200px", mx: "auto", width: "100%" }}>
                <Routes>
                    <Route path="/" element={<IntroductionSection />} />
                    <Route path="/architecture" element={<MaintenanceView title="Platform Architecture" description="Technical overview of the federated learning architecture and node communication." />} />
                    <Route path="/medical-data" element={<MaintenanceView title="Medical Data & Use Cases" description="Information on supported medical data formats and clinical use cases." />} />
                    <Route path="/security" element={<MaintenanceView title="Security & Encryption" description="Details on end-to-end encryption, differential privacy, and compliance standards." />} />
                    <Route path="/keycloak-setup" element={<KeycloakSetupGuide />} />
                    <Route path="/faq" element={<FAQSection />} />
                    <Route path="*" element={<MaintenanceView />} />
                </Routes>
            </Box>
        </Box>
         <Footer />
      </Box>
    </Box>
  );
};

export default Documentation;
